import type { Pricing } from '@totem/shared';

/**
 * Friendly (non-JSON) editor on top of the pricing predicate DSL
 * (`packages/shared/src/pricing/predicate.ts`). Only 5 of the DSL's 9 allowlisted
 * fields are exposed here — the other 4 are either redundant with a field the form
 * already has (`branch_id` — the rule row itself is already branch-scoped), broken for
 * range operators (`date` — always `false` against `between/gte/lte/gt/lt` because the
 * evaluator requires a `number`), or have no producing UI anywhere in the app today
 * (`customer.id`, `customer.category`). Those stay reachable only via "modo avanzado"
 * (the raw JSON textarea, unchanged).
 *
 * `predicateJsonToRows` is a best-effort REVERSE parse used when opening the edit
 * dialog: it returns `null` the moment it sees anything it cannot represent 1:1 (an
 * `or`/`not`, nested `and`, `in`/`nin`, or a field/op outside this allowlist) — the
 * caller must fall back to advanced/JSON mode in that case rather than silently
 * dropping part of an existing rule.
 */

export type FriendlyField =
  | 'pet.species'
  | 'pet.sex'
  | 'pet.breed'
  | 'pet.current_weight_grams'
  | 'pet.approximate_age_months';

export type FriendlyOp = 'eq' | 'neq' | 'gte' | 'lte' | 'gt' | 'lt' | 'between';

export type Inclusive = 'both' | 'min' | 'max' | 'none';

export type ValueKind = 'species' | 'sex' | 'text' | 'number_kg' | 'number';

export interface ConditionRow {
  id: string;
  field: FriendlyField;
  op: FriendlyOp;
  /** Used by eq/neq/gte/lte/gt/lt. Kg as typed by the user for `number_kg` fields. */
  value: string;
  /** Used by `between` only — kg as typed by the user for `number_kg` fields. */
  min: string;
  max: string;
  inclusive: Inclusive;
}

interface FieldMeta {
  field: FriendlyField;
  label_key: string;
  value_kind: ValueKind;
  ops: readonly FriendlyOp[];
}

const NUMERIC_OPS: readonly FriendlyOp[] = ['eq', 'neq', 'gte', 'lte', 'gt', 'lt', 'between'];

export const FRIENDLY_FIELDS: readonly FieldMeta[] = [
  { field: 'pet.species', label_key: 'pricing.field_species', value_kind: 'species', ops: ['eq', 'neq'] },
  { field: 'pet.sex', label_key: 'pricing.field_sex', value_kind: 'sex', ops: ['eq', 'neq'] },
  { field: 'pet.breed', label_key: 'pricing.field_breed', value_kind: 'text', ops: ['eq', 'neq'] },
  {
    field: 'pet.current_weight_grams',
    label_key: 'pricing.field_weight',
    value_kind: 'number_kg',
    ops: NUMERIC_OPS,
  },
  {
    field: 'pet.approximate_age_months',
    label_key: 'pricing.field_age',
    value_kind: 'number',
    ops: NUMERIC_OPS,
  },
];

const FIELD_META_BY_FIELD = new Map<FriendlyField, FieldMeta>(FRIENDLY_FIELDS.map((m) => [m.field, m]));

export const SPECIES_OPTIONS = [
  { value: 'dog', label_key: 'pricing.species_dog' },
  { value: 'cat', label_key: 'pricing.species_cat' },
] as const;

export const SEX_OPTIONS = [
  { value: 'male', label_key: 'pricing.sex_male' },
  { value: 'female', label_key: 'pricing.sex_female' },
  { value: 'unknown', label_key: 'pricing.sex_unknown' },
] as const;

export function fieldMeta(field: FriendlyField): FieldMeta {
  const meta = FIELD_META_BY_FIELD.get(field);
  if (!meta) throw new Error(`unknown_friendly_field:${field}`);
  return meta;
}

export function newConditionRow(): ConditionRow {
  return {
    id: cryptoRandomId(),
    field: 'pet.species',
    op: 'eq',
    value: 'dog',
    min: '',
    max: '',
    inclusive: 'both',
  };
}

/** Adjusts `op`/`value` defaults when the user switches a row's field. */
export function withFieldChanged(row: ConditionRow, field: FriendlyField): ConditionRow {
  const meta = fieldMeta(field);
  const op = meta.ops.includes(row.op) ? row.op : meta.ops[0]!;
  const value = meta.value_kind === 'species' ? 'dog' : meta.value_kind === 'sex' ? 'male' : '';
  return { ...row, field, op, value, min: '', max: '' };
}

/** `null` per row means "incomplete — cannot serialize yet" (surfaced by `validateRows`). */
export function validateRows(rows: readonly ConditionRow[]): string | null {
  for (const row of rows) {
    if (rowToLeaf(row) === null) return 'pricing.error_incomplete_condition';
  }
  return null;
}

export function rowsToPredicateJson(rows: readonly ConditionRow[]): string {
  const leaves = rows.map(rowToLeaf).filter((l): l is Pricing.Predicate => l !== null);
  if (leaves.length === 0) return JSON.stringify({ op: 'and', clauses: [] });
  if (leaves.length === 1) return JSON.stringify(leaves[0]);
  return JSON.stringify({ op: 'and', clauses: leaves });
}

/** Best-effort reverse parse. `null` = "not representable in the friendly builder". */
export function predicateJsonToRows(predicate_json: string): ConditionRow[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(predicate_json);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || !('op' in parsed)) return null;
  const leaves = flattenToLeaves(parsed as Pricing.Predicate);
  if (leaves === null) return null;
  const rows: ConditionRow[] = [];
  for (const leaf of leaves) {
    const row = leafToRow(leaf);
    if (row === null) return null;
    rows.push(row);
  }
  return rows;
}

export function summarizePredicate(
  predicate_json: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const rows = predicateJsonToRows(predicate_json);
  if (rows === null) return t('pricing.summary_advanced');
  if (rows.length === 0) return t('pricing.summary_always');
  return rows.map((row) => summarizeRow(row, t)).join(` ${t('pricing.summary_and')} `);
}

// ─── internals ───────────────────────────────────────────────────────────────

function rowToLeaf(row: ConditionRow): Pricing.Predicate | null {
  const meta = fieldMeta(row.field);
  if (row.op === 'between') {
    const min = parseNumericInput(row.min, meta.value_kind);
    const max = parseNumericInput(row.max, meta.value_kind);
    if (min === null || max === null) return null;
    return { op: 'between', field: row.field, min, max, inclusive: row.inclusive };
  }
  if (row.op === 'gte' || row.op === 'lte' || row.op === 'gt' || row.op === 'lt') {
    const value = parseNumericInput(row.value, meta.value_kind);
    if (value === null) return null;
    return { op: row.op, field: row.field, value };
  }
  // eq / neq
  if (meta.value_kind === 'number' || meta.value_kind === 'number_kg') {
    const value = parseNumericInput(row.value, meta.value_kind);
    if (value === null) return null;
    return { op: row.op, field: row.field, value };
  }
  const text = row.value.trim();
  if (!text) return null;
  return { op: row.op, field: row.field, value: text };
}

function flattenToLeaves(predicate: Pricing.Predicate): Pricing.Predicate[] | null {
  if (predicate.op === 'and') {
    const out: Pricing.Predicate[] = [];
    for (const clause of predicate.clauses) {
      if (clause.op === 'and' || clause.op === 'or' || clause.op === 'not') return null;
      out.push(clause);
    }
    return out;
  }
  if (predicate.op === 'or' || predicate.op === 'not') return null;
  return [predicate];
}

function leafToRow(leaf: Pricing.Predicate): ConditionRow | null {
  // A `switch` on the discriminant narrows `leaf` inside each case perfectly — the
  // equivalent chain of `if (leaf.op === 'x' || ...) return null;` guards does not
  // reliably narrow away the multi-value `PredicateBranch` members for TS here.
  switch (leaf.op) {
    case 'and':
    case 'or':
    case 'not':
    case 'in':
    case 'nin':
      return null;
    case 'between': {
      const meta = FIELD_META_BY_FIELD.get(leaf.field as FriendlyField);
      if (!meta?.ops.includes('between')) return null;
      const min = formatNumericForKind(leaf.min, meta.value_kind);
      const max = formatNumericForKind(leaf.max, meta.value_kind);
      if (min === null || max === null) return null;
      return {
        id: cryptoRandomId(),
        field: meta.field,
        op: 'between',
        value: '',
        min,
        max,
        inclusive: leaf.inclusive ?? 'both',
      };
    }
    case 'gte':
    case 'lte':
    case 'gt':
    case 'lt': {
      const meta = FIELD_META_BY_FIELD.get(leaf.field as FriendlyField);
      if (!meta?.ops.includes(leaf.op)) return null;
      const value = formatNumericForKind(leaf.value, meta.value_kind);
      if (value === null) return null;
      return { id: cryptoRandomId(), field: meta.field, op: leaf.op, value, min: '', max: '', inclusive: 'both' };
    }
    case 'eq':
    case 'neq': {
      const meta = FIELD_META_BY_FIELD.get(leaf.field as FriendlyField);
      if (!meta?.ops.includes(leaf.op)) return null;
      if (meta.value_kind === 'number' || meta.value_kind === 'number_kg') {
        if (typeof leaf.value !== 'number') return null;
        const value = formatNumericForKind(leaf.value, meta.value_kind);
        if (value === null) return null;
        return { id: cryptoRandomId(), field: meta.field, op: leaf.op, value, min: '', max: '', inclusive: 'both' };
      }
      if (typeof leaf.value !== 'string') return null;
      return {
        id: cryptoRandomId(),
        field: meta.field,
        op: leaf.op,
        value: leaf.value,
        min: '',
        max: '',
        inclusive: 'both',
      };
    }
  }
}

function parseNumericInput(raw: string, kind: ValueKind): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return kind === 'number_kg' ? Math.round(n * 1000) : Math.round(n);
}

function formatNumericForKind(value: number, kind: ValueKind): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (kind !== 'number_kg') return String(value);
  return trimTrailingZeros((value / 1000).toFixed(3));
}

function trimTrailingZeros(input: string): string {
  return input.includes('.') ? input.replace(/0+$/, '').replace(/\.$/, '') : input;
}

function summarizeRow(row: ConditionRow, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const field_label = t(fieldMeta(row.field).label_key);
  if (row.op === 'between') {
    const unit = row.field === 'pet.current_weight_grams' ? ' kg' : '';
    const incl = row.inclusive === 'both' ? '' : ` (${t(`pricing.summary_inclusive_${row.inclusive}`)})`;
    return `${field_label} ${t('pricing.summary_between')} ${row.min}–${row.max}${unit}${incl}`;
  }
  const op_label = t(`pricing.summary_op_${row.op}`);
  const unit = row.field === 'pet.current_weight_grams' ? ' kg' : '';
  return `${field_label} ${op_label} ${displayValueLabel(row, t)}${unit}`;
}

function displayValueLabel(row: ConditionRow, t: (key: string) => string): string {
  if (row.field === 'pet.species') {
    const known = SPECIES_OPTIONS.find((o) => o.value === row.value);
    return known ? t(known.label_key) : row.value;
  }
  if (row.field === 'pet.sex') {
    const known = SEX_OPTIONS.find((o) => o.value === row.value);
    return known ? t(known.label_key) : row.value;
  }
  return row.value;
}

function cryptoRandomId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
